import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
	AbcdQuestionDesktop,
	AbcdQuestionMobile,
	AssociationQuestionDesktop,
	AssociationQuestionMobile,
	ConnectionQuestionDesktop,
	ConnectionQuestionMobile,
	EstimationQuestionDesktop,
	EstimationQuestionMobile,
	StandardQuestionDesktop,
	StandardQuestionMobile,
	VisualQuestionDesktop,
	VisualQuestionMobile,
} from '@quizzach/layouts';
import { Colors } from '@quizzach/styles';
import { QuestionType } from '@quizzach/interfaces';
import { usePreviewQuestion } from '@quizzach/api-hooks';
import { scrollToTop } from '@quizzach/utils';
import { useWindowListener } from '@quizzach/contexts';

import {
	AdminPreviewStage,
	PREVIEW_QUESTION_NUMBER,
	PREVIEW_TOTAL_QUESTIONS,
} from './AdminPreviewStage';

const QUESTION_COMPONENTS = {
	[QuestionType.standard]: {
		Mobile: StandardQuestionMobile,
		Desktop: StandardQuestionDesktop,
	},
	[QuestionType.abcd]: {
		Mobile: AbcdQuestionMobile,
		Desktop: AbcdQuestionDesktop,
	},
	[QuestionType.association]: {
		Mobile: AssociationQuestionMobile,
		Desktop: AssociationQuestionDesktop,
	},
	[QuestionType.connection]: {
		Mobile: ConnectionQuestionMobile,
		Desktop: ConnectionQuestionDesktop,
	},
	[QuestionType.visual]: {
		Mobile: VisualQuestionMobile,
		Desktop: VisualQuestionDesktop,
	},
	[QuestionType.estimation]: {
		Mobile: EstimationQuestionMobile,
		Desktop: EstimationQuestionDesktop,
	},
} as const;

export const AdminQuizQuestionPreview = () => {
	const { id } = useParams<{ id: string }>();
	const { question, neighbors } = usePreviewQuestion(id);
	const navigate = useNavigate();
	const { touchDevice } = useWindowListener();

	// Browsing runs question → answer → next question through the whole
	// database, so back from a question lands on the previous answer.
	const goToAnswer = () => {
		navigate(`/preview/answer/${id}`);
	};
	const goToPreviousAnswer = () => {
		if (neighbors?.prev != null) navigate(`/preview/answer/${neighbors.prev}`);
	};

	useEffect(() => {
		scrollToTop();
	}, [id]);

	if (!question) return null;

	const renderQuestionComponent = () => {
		if (!question.type || !QUESTION_COMPONENTS[question.type]) {
			return null;
		}

		const componentVariant = touchDevice ? 'Mobile' : 'Desktop';
		const QuestionComponent = QUESTION_COMPONENTS[question.type][
			componentVariant
		] as any;

		// The redesigned stage has no boxed question containers — text sits
		// directly on the framed-cover backdrop.
		if (question.type === QuestionType.estimation) {
			return (
				<QuestionComponent color={Colors.none} currentQuestion={question} />
			);
		}

		return (
			<QuestionComponent
				color={Colors.none}
				currentQuestionNumber={neighbors?.position ?? PREVIEW_QUESTION_NUMBER}
				currentQuestion={question}
				totalQuestions={neighbors?.total ?? PREVIEW_TOTAL_QUESTIONS}
			/>
		);
	};

	return (
		<AdminPreviewStage
			question={question}
			dataId='slides-question-preview'
			onNext={goToAnswer}
			onPrevious={goToPreviousAnswer}
			position={neighbors?.position}
			total={neighbors?.total}
		>
			{renderQuestionComponent()}
		</AdminPreviewStage>
	);
};
